import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowRight,
  Radar,
  Search,
  Workflow,
  Activity,
  Sparkles,
  Eye,
  Fingerprint,
  Megaphone,
} from "lucide-react";
import { AuroraBackground } from "@/components/aurora-background";
import { GlassOrb } from "@/components/glass-orb";
import { GlassIcon } from "@/components/glass-icon";
import { SectionEyebrow } from "@/components/section-eyebrow";
import { MetricCounters } from "@/components/metric-counters";
import { OrbitLoop } from "@/components/orbit-loop";
import { TestimonialsMasonry } from "@/components/testimonials-masonry";
import { LandingHeader } from "@/components/landing-header";
import { TrustStrip } from "@/components/trust-strip";
import { Input } from "@/components/ui/input";
import { seo } from "@/lib/seo";
import { breadcrumb, webPage } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";
import { signUpUrl, scrollToSection } from "@/lib/cta";

const LP_PATH = "/lp/direct-smb";
const LP_TITLE = "Marketing Done For You — AI Autopilot Growth";
const LP_DESCRIPTION =
  "Automate your SEO, AI Search presence, and social media so you can focus on running your business. A continuous, high-velocity growth engine working 24/7 — at a fraction of the cost of an agency.";

export const Route = createFileRoute("/lp/direct-smb")({
  head: () =>
    seo({
      title: LP_TITLE,
      description: LP_DESCRIPTION,
      path: LP_PATH,
      type: "website",
      jsonLd: [
        webPage({
          name: LP_TITLE,
          description: LP_DESCRIPTION,
          url: `${SITE_URL}${LP_PATH}`,
        }),
        breadcrumb([
          { name: "Home", url: "/" },
          { name: LP_TITLE, url: LP_PATH },
        ]),
      ],
    }),
  component: LandingPage,
});

const HERO_SIGNUP = signUpUrl("lp-direct-smb-hero");
const FINAL_SIGNUP = signUpUrl("lp-direct-smb-final");
const HEADER_SIGNUP = signUpUrl("lp-direct-smb-header");

const PILLARS = [
  {
    label: "24/7 Market Tracking",
    description:
      "Axy.digital constantly monitors your specific industry, local market trends, and target customer behaviors online. It always knows exactly what your buyers are looking for — keeping your business positioned to capture traffic without you lifting a finger.",
    icon: Eye,
  },
  {
    label: "Full Business Personalization",
    description:
      "No generic templates or robotic AI slop. By deeply integrating your actual business details, unique services, location parameters, and core message, Axy.digital creates marketing assets that sound genuinely unique and authentic to your brand.",
    icon: Fingerprint,
  },
  {
    label: "Autopilot Multi-Channel Publishing",
    description:
      "From ranking on Google and showing up as a recommendation in ChatGPT, to maintaining consistent, active social media profiles — the system writes, schedules, and automatically publishes your content across every essential channel.",
    icon: Megaphone,
  },
];

const FINAL_MATRIX = [
  {
    label: "Time Reclaimed",
    value: "60+ Hours Saved/Mo",
    proof: "Automated Content & Strategy",
  },
  {
    label: "Cost Efficiency",
    value: "99% Retainer Savings",
    proof: "Zero Agency Overhead Ever Again",
  },
  {
    label: "Launch Velocity",
    value: "Active in 120 Seconds",
    proof: "Zero Marketing Experience Needed",
  },
];

function VisibilityScanForm() {
  const [domain, setDomain] = useState("");
  const [email, setEmail] = useState("");

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const url = new URL(signUpUrl("lp-direct-smb-visibility-scan"));
    if (domain) url.searchParams.set("domain", domain);
    if (email) url.searchParams.set("email", email);
    window.location.href = url.toString();
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 w-full">
      <Input
        type="text"
        required
        placeholder="yourcompany.com"
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
        className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40 flex-1"
      />
      <Input
        type="email"
        required
        placeholder="business@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40 flex-1"
      />
      <button
        type="submit"
        className="btn-gradient hover:brightness-110 transition-all rounded-full px-6 h-12 text-sm font-medium inline-flex items-center justify-center gap-2 whitespace-nowrap"
      >
        Generate My Free AI Plan
        <ArrowRight className="size-4" />
      </button>
    </form>
  );
}

function LandingPage() {
  return (
    <>
      <LandingHeader ctaLabel="Get Started" ctaHref={HEADER_SIGNUP} />

      {/* HERO */}
      <section className="relative px-6 pt-20 pb-24 overflow-hidden">
        <AuroraBackground />
        <div
          aria-hidden
          className="absolute left-1/2 top-24 -translate-x-1/2 opacity-60 pointer-events-none"
        >
          <GlassOrb size="lg" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <SectionEyebrow className="justify-center text-center">
            Done-for-you marketing on autopilot
          </SectionEyebrow>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-white text-balance mb-6">
            Marketing Done For You.
          </h1>
          <p className="text-lg md:text-xl text-white/75 max-w-3xl mx-auto mb-10 text-pretty">
            Automate your SEO, AI Search presence, and social media channels so you can focus
            entirely on running your business. A continuous, high-velocity growth engine working for
            you 24/7 — at a fraction of the cost of hiring a human team or agency.
          </p>
          <div className="flex flex-col items-center gap-3">
            <a
              href={HERO_SIGNUP}
              className="btn-gradient hover:-translate-y-px hover:brightness-110 transition-all rounded-full px-6 py-3 text-sm md:text-base font-medium inline-flex items-center gap-2"
            >
              Get Started (Free · 2 Mins)
              <ArrowRight className="size-4" />
            </a>
            <p className="text-xs text-white/55">
              Engine live • 1,500+ businesses • No credit card required
            </p>
          </div>
        </div>
      </section>

      <TrustStrip />

      {/* METRICS BANNER */}
      <section className="px-6 py-20">
        <div className="max-w-6xl mx-auto">
          <SectionEyebrow>Hands-free business growth</SectionEyebrow>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance mb-3 max-w-3xl">
            A 24/7 marketing team that costs less than one freelancer — and never sleeps.
          </h2>
          <p className="text-white/65 max-w-2xl mb-10">
            Built for busy business owners who'd rather run their business than write blog posts,
            wrestle with automation tools, or chase agencies for monthly reports.
          </p>
          <MetricCounters
            items={[
              {
                value: 60,
                prefix: "+",
                suffix: " hrs",
                label: "Saved monthly",
                proof: "Automated content & strategy",
              },
              {
                value: 99,
                suffix: "%",
                label: "Retainer savings",
                proof: "Vs. hiring an agency",
              },
              {
                value: 11,
                suffix: "×",
                label: "AI visibility growth",
                proof: "Across 90-day rollouts",
              },
              {
                value: 40,
                prefix: "+",
                suffix: "%",
                label: "Referral traffic",
                proof: "Within 90 days of deployment",
              },
            ]}
          />
        </div>
      </section>

      {/* 3 PILLARS */}
      <section className="relative px-6 py-24 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(60% 40% at 30% 20%, rgba(123,63,242,0.18), transparent 60%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto">
          <SectionEyebrow>The Axy.digital autopilot</SectionEyebrow>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance mb-6 max-w-3xl">
            Hands-Free Business Growth with Axy.digital's AI agents.
          </h2>
          <p className="text-white/75 leading-relaxed text-pretty max-w-3xl mb-12">
            Three always-on agents do the research, the writing, and the publishing so you don't
            have to touch a single tool.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PILLARS.map((p, i) => (
              <div
                key={p.label}
                className="glass-card p-6 hover-lift hover:-translate-y-1 relative"
              >
                <div className="flex items-center justify-between mb-4">
                  <GlassIcon icon={p.icon} />
                  <span className="text-xs uppercase tracking-wider text-white/40 tabular-nums">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-white mb-2">{p.label}</h3>
                <p className="text-sm text-white/65 leading-relaxed">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ORBIT LOOP */}
      <section className="px-6 py-24">
        <div className="max-w-6xl mx-auto text-center mb-12">
          <SectionEyebrow className="justify-center">
            Your hands-free growth engine in 5 simple steps
          </SectionEyebrow>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance max-w-3xl mx-auto mb-4">
            From research to publishing — handled for you.
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto">
            Axy.digital replaces fragmented marketing tasks with a singular, always-on execution
            system that handles everything from initial research to automated publishing.
          </p>
        </div>
        <div className="max-w-5xl mx-auto">
          <OrbitLoop
            steps={[
              {
                label: "Find What Customers Want",
                description:
                  "Axy.digital continuously scans customer search patterns to surface the exact topics, questions, and services your target buyers care about right now.",
                icon: Radar,
              },
              {
                label: "Locate Missing Traffic",
                description:
                  "The engine instantly maps out exactly where your website is showing up online and pinpoints the exact areas where your competitors are dominating.",
                icon: Search,
              },
              {
                label: "Launch Campaigns Hands-Free",
                description:
                  "The system automatically drafts, optimizes, and deploys high-performing website content and multi-channel campaign assets directly to your platforms.",
                icon: Workflow,
              },
              {
                label: "Track Your Real Performance",
                description:
                  "Ditch complex analytics tools. View your entire digital footprint and incoming traffic growth inside a single, radically simple performance dashboard.",
                icon: Activity,
              },
              {
                label: "Automatic System Updates",
                description:
                  "Run an always-on marketing layer that automatically learns and refines its strategies based on new search trends, performance data, and engine updates.",
                icon: Sparkles,
              },
            ]}
          />
          <div className="mt-8 flex justify-center">
            <span className="glass-pill px-4 py-2 text-xs text-white/85 inline-flex items-center gap-2">
              <span className="size-1.5 rounded-full bg-pink-soft animate-pulse" />0 manual steps ·
              Engine running on autopilot
            </span>
          </div>
        </div>
      </section>

      {/* VISIBILITY SCAN — SECONDARY CTA */}
      <section id="visibility-scan-zone" className="px-6 py-20 scroll-mt-20">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card p-8 md:p-12">
            <SectionEyebrow>Free AI automation blueprint</SectionEyebrow>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance mb-4">
              🤖 Curious exactly how AI would automate your specific business?
            </h2>
            <p className="text-white/70 max-w-2xl mb-8 text-pretty">
              Figuring out prompts or complex workflows is hard. Enter your company domain below and
              our autonomous engine will instantly generate a tailored 12-month marketing blueprint
              — showing you exactly which search gaps, content hubs, and social channels Axy.digital
              can run for you on autopilot.
            </p>
            <VisibilityScanForm />
            <p className="mt-4 text-xs text-white/50">
              We'll email your tailored automation plan within minutes.
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialsMasonry
        eyebrow="Business owners leveling the playing field with AI"
        heading="Built for owners who'd rather run their business than their marketing stack."
        items={[
          {
            quote:
              "Axy.digital helped us 5x our content output without sounding generic. The content is detailed, sharp, and actually reflects how we want to show up as an early-stage team.",
            name: "Aurelien Vasinis",
            role: "Founder & CEO",
            company: "Kuration AI",
            initials: "AV",
            hue: 295,
            metric: { value: "60+ hrs", label: "saved monthly" },
            featured: true,
          },
          {
            quote:
              "In 90 days, Axy.digital helped us go from invisible in AI answers to ranking #3 against incumbents with a decade of SEO dominance.",
            name: "Savannah Thatcher",
            role: "CMO",
            company: "Wingbits",
            initials: "ST",
            hue: 320,
            metric: { value: "11×", label: "AI visibility growth" },
          },
          {
            quote:
              "The depth of the content Axy.digital generated from very little input was genuinely impressive. Not having to prompt or configure everything manually was a game changer.",
            name: "Florian Rennberger",
            role: "Head of Sales",
            company: "Conducttr Germany",
            initials: "FR",
            hue: 270,
          },
        ]}
      />

      {/* FINAL CTA */}
      <section className="relative px-6 py-24 border-t border-neutral-800 bg-neutral-950 overflow-hidden">
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="flex justify-center mb-8">
            <span className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full border border-emerald-400/25 bg-emerald-400/5 text-[11px] uppercase tracking-[0.18em] text-emerald-300/90 font-mono">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex size-2 rounded-full bg-emerald-400" />
              </span>
              Account Setup Node: Ready
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white text-balance mb-5">
            Let Axy.digital do the heavy lifting for you.
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto mb-12 text-pretty">
            Stop spending your nights writing content drafts, wrestling with complex automation
            workflows, or chasing expensive agencies. Turn your growth into an automated utility
            that works for you around the clock.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 border border-white/10 rounded-2xl overflow-hidden mb-12 bg-white/[0.02]">
            {FINAL_MATRIX.map((m, i) => (
              <div
                key={m.label}
                className={[
                  "px-5 py-6 text-left",
                  i < FINAL_MATRIX.length - 1 ? "md:border-r border-white/10" : "",
                  i < FINAL_MATRIX.length - 1 ? "border-b md:border-b-0" : "",
                ].join(" ")}
              >
                <div className="text-xs uppercase tracking-wider text-white/45 mb-2">{m.label}</div>
                <div className="text-2xl md:text-3xl font-semibold text-white mb-1">{m.value}</div>
                <div className="text-xs text-neutral-400">{m.proof}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-col items-center gap-3">
            <a
              href={FINAL_SIGNUP}
              className="btn-gradient hover:-translate-y-px hover:brightness-110 transition-all rounded-full px-7 py-3.5 text-sm md:text-base font-medium inline-flex items-center gap-2"
            >
              Get Started (Free · 2 Mins)
              <ArrowRight className="size-4" />
            </a>
            <p className="text-xs text-white/55">
              Free setup includes a free AI search visibility map and market research. No complex
              setup.
            </p>
          </div>

          <div className="mt-10 text-sm text-white/55">
            Want to check your business's current online visibility ranking first?{" "}
            <button
              type="button"
              onClick={() => scrollToSection("visibility-scan-zone")}
              className="text-white/85 underline underline-offset-4 hover:text-white transition-colors"
            >
              Run Free Visibility Scan
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
